/**
 * Klartext + Farbe für Geliver-Sendungsstatus.
 *
 * 29.07.2026, Punkt 3 aus dem Durchlauf des Betreibers: die Spalte „Durum"
 * zeigte den Rohwert (`PRE_TRANSIT`) in einem praktisch unlesbaren hellgrauen
 * Feld. Zwei Ursachen, beide hier behoben:
 *
 *  1. Es gab nur eine FARB-Map und keine Text-Map — gerendert wurde der Code
 *     selbst. `STATUS_FILTERS` in geliver-sendungen.tsx trägt zwar Labels,
 *     deckt aber nur die 7 Filter-Zustände ab; alles andere fiel durch.
 *  2. Die Farben waren reine Dunkelmodus-Werte (`text-slate-300` auf
 *     `bg-slate-500/20`). Im hellen Panel ergibt das Grau auf Grau. Jede
 *     Stufe hat jetzt eine Hell-Variante, nach dem Muster der
 *     Fatura-Vorschau (`text-violet-700 dark:text-violet-400`)
 *     ⇄ feedback_theme_tokens_not_hardcoded_colors.
 *
 * `PRE_TRANSIT` fehlte in der Farb-Map ganz und landete im grauen Rückfall —
 * genau der Zustand, den der Betreiber vor sich hatte.
 *
 * Zwei Wertebereiche treffen sich hier, weil `effectiveStatus()` sie
 * zusammenführt (`trackingStatus.trackingStatusCode || statusCode`). Beide
 * Listen stehen dokumentiert in packages/api/src/lib/geliver-polling.ts:79-86:
 *   trackingStatusCode: PRE_TRANSIT | TRANSIT | DELIVERED | FAILURE
 *                       | RETURNED | CANCELED | UNKNOWN
 *   statusCode:         CREATED | GOT_OFFERS | OFFER_ACCEPTED
 *                       | TRACKING_CODE_CREATED | LABEL_PRINTED | SHIPPED
 *                       | DELIVERED | CANCELED | RETURNED | RETURN_SHIPPED
 *                       | FAILED
 *
 * Die Labels sind Getter (`() => m.x()`), nicht Werte: ein `m.x()` außerhalb
 * einer Komponente friert auf der Basissprache ein (Begründung steht in
 * packages/ui/src/components/shipping/geliver-tab-bar.tsx).
 */
import * as m from '../paraglide/messages'

const STATUS_LABEL: Record<string, () => string> = {
  CREATED: () => m.geliver_status_created(),
  GOT_OFFERS: () => m.geliver_status_got_offers(),
  OFFER_ACCEPTED: () => m.geliver_status_offer_accepted(),
  TRACKING_CODE_CREATED: () => m.geliver_status_tracking_code_created(),
  LABEL_PRINTED: () => m.geliver_status_label_printed(),
  SHIPPED: () => m.geliver_status_shipped(),
  DELIVERED: () => m.geliver_status_delivered(),
  CANCELED: () => m.geliver_status_canceled(),
  RETURNED: () => m.geliver_status_returned(),
  RETURN_SHIPPED: () => m.geliver_status_return_shipped(),
  FAILED: () => m.geliver_status_failed(),
  PRE_TRANSIT: () => m.geliver_status_pre_transit(),
  TRANSIT: () => m.geliver_status_transit(),
  FAILURE: () => m.geliver_status_failure(),
  UNKNOWN: () => m.geliver_status_unknown(),
}

const STATUS_BADGE: Record<string, string> = {
  CREATED: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30',
  GOT_OFFERS: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  OFFER_ACCEPTED: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  TRACKING_CODE_CREATED: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
  LABEL_PRINTED: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  PRE_TRANSIT: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30',
  SHIPPED: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  TRANSIT: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  DELIVERED: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  CANCELED: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
  RETURNED: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  RETURN_SHIPPED: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  FAILED: 'bg-red-600/20 text-red-700 dark:text-red-200 border-red-600/40',
  FAILURE: 'bg-red-600/20 text-red-700 dark:text-red-200 border-red-600/40',
  UNKNOWN: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30',
}

const FALLBACK_BADGE = STATUS_BADGE.UNKNOWN

/**
 * Lesbarer Text für einen Statuscode. Unbekannte Codes fallen auf den Code
 * selbst zurück — lieber ein Rohwert als ein leeres Feld, und der Rückfall
 * macht einen neuen Geliver-Zustand im Panel sofort sichtbar.
 */
export function geliverStatusLabel(code: string): string {
  return STATUS_LABEL[code]?.() ?? code
}

export function geliverStatusBadgeClass(code: string): string {
  return STATUS_BADGE[code] ?? FALLBACK_BADGE
}
